import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { usePageTitle } from "@/hooks/use-page-title";
import { useSubscription } from "@/hooks/queries/use-billing";
import { cn } from "@/lib/utils";
import { AlertTriangle, CheckCircle2, XCircle } from "lucide-react";
import { useSearchParams } from "react-router-dom";
import { PlanCard } from "@/components/billing/plan-card";
import { useOnboardingState } from "@/hooks/queries/use-onboarding";
import type { SubscriptionStatus } from "@mycompbox/shared";

/**
 * How each subscription status is presented.
 *
 * Every status gets its own copy rather than sharing a generic "there is a
 * problem" message, because the four states call for genuinely different
 * actions: an `incomplete` customer never finished paying, a `past_due` one
 * has a card that failed, and a `canceled` one chose to leave. Telling all
 * three the same thing would leave each of them guessing.
 *
 * The tone is deliberately non-accusatory. This screen is shown to people who
 * are usually blocked by an expired card, not by intent.
 */
const STATUS_PRESENTATION: Record<
  SubscriptionStatus,
  {
    label: string;
    headline: string;
    detail: string;
    action: string | null;
    tone: "positive" | "warning" | "critical";
  }
> = {
  active: {
    label: "Active",
    headline: "Your subscription is active",
    detail: "Your organization has full access to CompBox.",
    action: null,
    tone: "positive",
  },
  past_due: {
    label: "Payment failed",
    headline: "We could not process your last payment",
    detail:
      "Your organization is temporarily read-only. Everything you have already created remains visible, and full access returns as soon as a payment succeeds.",
    action: "Update payment method",
    tone: "warning",
  },
  canceled: {
    label: "Canceled",
    headline: "Your subscription has ended",
    detail:
      "Your organization is read-only. Your work is still here and still readable — resubscribing restores write access immediately.",
    action: "Resubscribe",
    tone: "critical",
  },
  incomplete: {
    label: "Setup incomplete",
    headline: "Your subscription was never completed",
    detail:
      "Checkout was started but not finished, so this organization does not have access yet. Completing checkout activates it right away.",
    action: "Complete checkout",
    tone: "critical",
  },
};

const TONE_CLASSES: Record<"positive" | "warning" | "critical", string> = {
  positive: "text-emerald-400",
  warning: "text-amber-400",
  critical: "text-red-400",
};

function StatusIcon({ tone }: { tone: "positive" | "warning" | "critical" }) {
  const className = cn("h-5 w-5", TONE_CLASSES[tone]);
  if (tone === "positive") return <CheckCircle2 className={className} />;
  if (tone === "warning") return <AlertTriangle className={className} />;
  return <XCircle className={className} />;
}

/** Renders a date, tolerating a malformed value rather than crashing the page. */
function formatDate(iso: string): string | null {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) {
    return null;
  }
  return date.toLocaleDateString(undefined, {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
}

export function BillingPage() {
  usePageTitle("Billing");
  const { data, isLoading, isError } = useSubscription();
  const { data: onboarding } = useOnboardingState();
  const isAdmin = onboarding?.step === "ready" && onboarding.organizationRole === "admin";
  const [searchParams] = useSearchParams();
  const checkoutDone = searchParams.get("checkout") === "success";

  if (isLoading) {
    return (
      <div className="space-y-8" data-testid="billing-loading">
        <h1 className="text-lg font-medium">Billing</h1>
        <div className="flex items-center justify-center py-16">
          <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-primary" />
        </div>
      </div>
    );
  }

  // A blocked customer landing here needs a way forward even when the read
  // fails, so this states what is known and does not pretend to know a status.
  if (isError || !data) {
    return (
      <div className="space-y-8" data-testid="billing-error">
        <h1 className="text-lg font-medium">Billing</h1>
        <Card>
          <CardHeader>
            <CardTitle>We could not load your subscription</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm text-muted-foreground">
              This is usually temporary. If it persists, contact support and we
              will sort it out directly — your data is unaffected.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // An org-less caller (the bootstrap admin) has no subscription at all. Saying
  // so is more honest than rendering a fabricated "active" state.
  if (data.organizationless) {
    return (
      <div className="space-y-8" data-testid="billing-organizationless">
        <h1 className="text-lg font-medium">Billing</h1>
        <Card>
          <CardHeader>
            <CardTitle>No organization</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground">
              Your account is not attached to an organization, so there is no
              subscription to manage. Access is unrestricted.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const presentation = STATUS_PRESENTATION[data.subscriptionStatus];
  const periodEnd = data.currentPeriodEnd ? formatDate(data.currentPeriodEnd) : null;

  return (
    <div className="space-y-8" data-testid="billing-page">
      <div className="space-y-2">
        <h1 className="text-lg font-medium">Billing</h1>
        <p className="text-muted-foreground">
          Your organization&rsquo;s subscription and access level.
        </p>
      </div>

      {checkoutDone && (
        <p
          role="status"
          className="rounded-lg border border-emerald-500/30 bg-emerald-500/[0.07] px-4 py-2.5 text-sm"
        >
          Thanks — your payment details are saved. It can take a minute for this page to update.
        </p>
      )}

      <PlanCard subscription={data} isAdmin={isAdmin} />

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <StatusIcon tone={presentation.tone} />
            <span>{presentation.headline}</span>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-sm">
            <span className="text-muted-foreground">
              Status:{" "}
              <span
                className={cn("font-medium", TONE_CLASSES[presentation.tone])}
                data-testid="billing-status-label"
              >
                {presentation.label}
              </span>
            </span>
            <span className="text-muted-foreground">
              Seats: <span className="font-medium text-foreground">{data.seatQuantity}</span>
            </span>
            {periodEnd && (
              <span className="text-muted-foreground" data-testid="billing-period-end">
                {/* Wording follows the status: the same timestamp is a renewal
                    date for a healthy subscription and an end date for a dead
                    one, and calling a lapse date a "renewal" would be wrong. */}
                {data.subscriptionStatus === "active" ? "Renews" : "Period ended"}:{" "}
                <span className="font-medium text-foreground">{periodEnd}</span>
              </span>
            )}
          </div>

          <p className="text-sm text-muted-foreground">{presentation.detail}</p>

        </CardContent>
      </Card>
    </div>
  );
}
